import { AikoTour, AikoAnchor, useAikoTour, Button, type TourStep } from "@aisquare/ui";
import { Compass, Sparkles, Users, Workflow } from "lucide-react";
import { Section, VariantBlock } from "../lib/Section";

/**
 * A small page of mock targets, mirroring the unified Home surfaces a real tour
 * would highlight (composer / CREATE-CONNECT-GOVERN tiles / workspace pill).
 * Each target is wrapped in `<AikoAnchor>`, which stamps `data-aiko-anchor`
 * onto the child so the tour can resolve it without a ref. Ids are namespaced
 * to this demo so they cannot collide with other demos on the same page.
 */
function MockSurface() {
  return (
    <div className="flex w-full max-w-md flex-col gap-4">
      <AikoAnchor id="aikotour-demo.composer">
        <div className="rounded-xl border border-border bg-background p-4">
          <div className="text-sm font-semibold text-foreground">Composer</div>
          <div className="text-xs text-muted-foreground">
            Describe what you want to build.
          </div>
        </div>
      </AikoAnchor>

      <div className="grid grid-cols-3 gap-3">
        <AikoAnchor id="aikotour-demo.create">
          <button className="rounded-lg border border-border bg-background p-3 text-left text-sm font-medium text-foreground">
            Create
          </button>
        </AikoAnchor>
        <AikoAnchor id="aikotour-demo.connect">
          <button className="rounded-lg border border-border bg-background p-3 text-left text-sm font-medium text-foreground">
            Connect
          </button>
        </AikoAnchor>
        <AikoAnchor id="aikotour-demo.govern">
          <button className="rounded-lg border border-border bg-background p-3 text-left text-sm font-medium text-foreground">
            Govern
          </button>
        </AikoAnchor>
      </div>

      <AikoAnchor id="aikotour-demo.workspace">
        <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground">
          <Users className="h-3.5 w-3.5" aria-hidden /> Personal workspace
        </div>
      </AikoAnchor>
    </div>
  );
}

/**
 * Three steps exercising the three body shapes a step supports: a plain `body`,
 * a 2-column `grid` of mini-cards, and a `bullets` list. The first one set wins,
 * so each step here shows exactly one shape.
 */
const STEPS: TourStep[] = [
  {
    anchorId: "aikotour-demo.composer",
    title: "Start in the composer",
    body: "Tell Aiko what you want to make and it drafts the studio for you.",
    side: "bottom",
  },
  {
    anchorId: "aikotour-demo.create",
    title: "Create, Connect, Govern",
    side: "bottom",
    grid: [
      { icon: <Sparkles aria-hidden />, heading: "Create", body: "Spin up a studio." },
      { icon: <Workflow aria-hidden />, heading: "Connect", body: "Wire your sources." },
    ],
  },
  {
    anchorId: "aikotour-demo.workspace",
    title: "Switch workspaces",
    side: "top",
    bullets: [
      { icon: <Compass aria-hidden />, text: "Personal vs team, always one click away." },
      { icon: <Users aria-hidden />, text: "Team workspaces unlock studios and co-creators." },
    ],
  },
];

export function AikoTourDemo() {
  // `autoStart: false` keeps the full-screen spotlight from opening on page load
  // (every demo shares one scrolling page). A button drives it instead: reset()
  // clears any one-time-seen flag so the tour can replay, then start() opens it.
  // No storageKey here, so nothing is persisted - it runs every time you click.
  const tour = useAikoTour({ autoStart: false });

  return (
    <Section
      id="aiko-tour"
      title="AikoTour"
      description="A guided product tour: a portalled spotlight cutout over a target plus a pinned tooltip that walks through ordered steps. Targets are declared with AikoAnchor (data-attribute, no refs) and resolved via useAikoTarget; useAikoTour owns the open + one-time-seen lifecycle. Composes the stock Popover and the brand Button."
    >
      <VariantBlock
        label="Run the tour"
        caption="opens a full-screen spotlight; Next/Back to navigate, Esc or Skip to dismiss"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Button onClick={() => { tour.reset(); tour.start(); }}>Start tour</Button>
            <span className="text-xs text-muted-foreground">
              {tour.open ? "Tour running" : "3 steps over the targets below"}
            </span>
          </div>
          <MockSurface />
          <AikoTour steps={STEPS} {...tour.tourProps} />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Anchoring a target"
        caption="AikoAnchor stamps data-aiko-anchor onto its child via the Slot pattern"
      >
        <AikoAnchor id="aikotour-demo.standalone">
          <div className="rounded-xl border border-border bg-background p-4">
            <div className="text-sm font-semibold text-foreground">Publish</div>
            <div className="text-xs text-muted-foreground">
              Inspect this element to see data-aiko-anchor="aikotour-demo.standalone".
            </div>
          </div>
        </AikoAnchor>
      </VariantBlock>
    </Section>
  );
}
